import { useState } from 'react';
import type { Priority, ShoppingItem } from '../types';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { Mascot } from './mascots';
import { CollapsibleItems, DetailDisclosure } from './Expandable';
import styles from '../App.module.css';

export type ShoppingEditor = {
  stageId: string;
  hideItem: (stageId: string, itemId: string) => void;
  removeCustomItem: (stageId: string, itemId: string) => void;
  renameItem: (stageId: string, itemId: string, name: string, isCustom: boolean) => void;
  addItem: (input: {
    stageId: string;
    name: string;
    priority?: Priority;
    timing?: string;
    note?: string;
  }) => void;
};

interface ShoppingProps {
  items: ShoppingItem[];
  editor?: ShoppingEditor;
}

export function Shopping({ items, editor }: ShoppingProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftPriority, setDraftPriority] = useState<Priority>('should');

  function formatPrice(yen: number): string {
    if (yen === 0) return ui.shopping.free;
    return `¥${new Intl.NumberFormat('ja-JP').format(yen)}`;
  }

  function priceRange(min: number, max: number): string {
    if (min === 0 && max === 0) return ui.shopping.free;
    if (min === max) return formatPrice(min);
    return `${formatPrice(min)}〜${formatPrice(max)}`;
  }

  function handleDelete(itemId: string, isCustom: boolean) {
    if (!editor) return;
    if (!window.confirm(ui.shopping.deleteConfirm)) return;
    if (isCustom) editor.removeCustomItem(editor.stageId, itemId);
    else editor.hideItem(editor.stageId, itemId);
  }

  function handleRename(itemId: string, current: string, isCustom: boolean) {
    if (!editor) return;
    const next = window.prompt(ui.shopping.editItem, current);
    if (next == null) return;
    editor.renameItem(editor.stageId, itemId, next, isCustom);
  }

  function handleAdd() {
    if (!editor || !draftName.trim()) return;
    editor.addItem({
      stageId: editor.stageId,
      name: draftName,
      priority: draftPriority,
    });
    setDraftName('');
  }

  return (
    <div className={styles.shopping}>
      <div className={styles.shoppingHead}>
        <Mascot id="shop" size={36} />
        <p className={styles.shoppingNote}>{ui.shopping.note}</p>
      </div>

      {editor ? (
        <div className={styles.editToolbar}>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? ui.shopping.doneEdit : ui.shopping.editMode}
          </button>
        </div>
      ) : null}

      <CollapsibleItems total={items.length} initial={editing ? items.length : 4}>
        {(visible) => (
          <ul className={styles.shoppingList}>
            {items.slice(0, visible).map((item) => (
              <li key={item.id} className={styles.shoppingItem}>
                <div className={styles.shoppingMain}>
                  <span
                    className={`${styles.priorityBadge} ${styles[`priority${item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}`]}`}
                  >
                    {ui.shopping.priorityLabels[item.priority]}
                  </span>
                  <span className={styles.shoppingName}>
                    {item.name}
                    {item.custom ? (
                      <span className={styles.customBadge}>{ui.shopping.customBadge}</span>
                    ) : null}
                  </span>
                </div>
                <div className={styles.shoppingMeta}>
                  <span className={styles.shoppingTiming}>
                    {ui.shopping.timing} {item.timing}
                  </span>
                  <span className={styles.shoppingPrice}>
                    {priceRange(item.priceYenMin, item.priceYenMax)}
                  </span>
                </div>
                {item.note ? <DetailDisclosure detail={item.note} /> : null}
                {editing && editor ? (
                  <div className={styles.itemEditActions}>
                    <button
                      type="button"
                      className={styles.tinyButton}
                      onClick={() => handleRename(item.id, item.name, Boolean(item.custom))}
                    >
                      {ui.shopping.editItem}
                    </button>
                    <button
                      type="button"
                      className={styles.tinyButtonDanger}
                      onClick={() => handleDelete(item.id, Boolean(item.custom))}
                    >
                      {ui.shopping.deleteItem}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CollapsibleItems>

      {editing && editor ? (
        <div className={styles.addItemBox}>
          <p className={styles.addItemTitle}>{ui.shopping.addItem}</p>
          <label className={styles.onboardingField}>
            <span>{ui.shopping.priorityLabels.should}</span>
            <select
              className={styles.onboardingInput}
              value={draftPriority}
              onChange={(e) => setDraftPriority(e.target.value as Priority)}
            >
              {(['must', 'should', 'nice'] as Priority[]).map((p) => (
                <option key={p} value={p}>
                  {ui.shopping.priorityLabels[p]}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.addItemRow}>
            <input
              className={styles.onboardingInput}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={ui.shopping.addPlaceholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <button
              type="button"
              className={styles.primaryButton}
              disabled={!draftName.trim()}
              onClick={handleAdd}
            >
              {ui.shopping.addButton}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
